import React, { useState } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import { jiraApiRef } from '../jira/api';
import { useApi } from '@backstage/core-plugin-api';
import { FiExternalLink, FiMessageSquare } from 'react-icons/fi';

// Modal accessibility requirement
Modal.setAppElement('#root');

// Configuration
const FORMS_URL = "https://forms.office.com/e/YwJPRc5fJE"; // Updated link for the form
const PROJECT_KEY = 'CPA'; // CPA - Project ID 25302

const FloatingButton: React.FC = () => {
  // States
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isJiraFormVisible, setIsJiraFormVisible] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // API Reference
  const jiraApi = useApi(jiraApiRef);
  
  // Handlers
  const openModal = () => setModalIsOpen(true);

  const closeModal = () => {
    setModalIsOpen(false);
    setIsJiraFormVisible(false);
    setTitle('');
    setDescription('');
    setError('');
  };

  const handleJiraButtonClick = () => setIsJiraFormVisible(true);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await jiraApi.createJiraTicket({
        projectKey: PROJECT_KEY,
        summary: title,
        description,
      });
      alert('Jira ticket created successfully!');
      closeModal();
    } catch (error) {
      console.error('Failed to create Jira ticket:', error);
      alert('Error creating Jira ticket.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button onClick={openModal}>
        <FiMessageSquare size={32} />
      </Button>

      <StyledModal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Actions"
        shouldCloseOnOverlayClick={true}
        style={{ overlay: { background: 'transparent' } }}
      >
        <ModalContent>
          {!isJiraFormVisible ? (
            <>
              <ActionButton>
                <a
                  href={FORMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', color: '#fff' }}
                >
                  Give Feedback <FiExternalLink style={{ marginLeft: '5px' }} />
                </a>
              </ActionButton>
              <ActionButton onClick={handleJiraButtonClick}>
                Report an Issue
              </ActionButton>
            </>
          ) : (
            <Form onSubmit={handleFormSubmit}>
              <span><b>Title:</b></span>
              <Input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
              <span><b>Description:</b></span>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
              />
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Creating...' : <> Create Jira Ticket</>}
              </SubmitButton>
            </Form>
          )}
        </ModalContent>
      </StyledModal>
    </>
  );
};

export default FloatingButton;

// Styles
const Button = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #000;
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.3s;

  &:hover {
    background-color: #cc0000;
  }
`;

const StyledModal = styled(Modal)`
  &.ReactModal__Content {
    position: absolute;
    bottom: 80px;
    right: 40px;
    width: 250px;
    padding: 15px;
    border-radius: 12px;
    background-color: #fff;
    box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.1);
    border: none;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ActionButton = styled.button`
  background-color: #e60000;
  color: #fff;
  border: none;
  padding: 4px;
  border-radius: 6px;
  cursor: pointer;
  text-align: center;

  &:hover {
    background-color: #cc0000;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 2px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background-color: #f9f9f9;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: border-color 0.3s;

  &:focus {
    border-color: #e60000;
    outline: none;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 16px;
  background-color: #f9f9f9;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: border-color 0.3s;
  resize: vertical;
  min-height: 100px;
  margin-bottom: 8px;

  &:focus {
    border-color: #e60000;
    outline: none;
  }
`;

const SubmitButton = styled.button`
  background-color: #e60000;
  color: #fff;
  border: none;
  padding: 4px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    background-color: #cc0000;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: red;
  margin-top: -5px;
`;
